import React, { useState } from "react";
import { createUserWithEmailAndPassword, signOut } from "firebase/auth";
import { doc, setDoc } from "firebase/firestore";
import { auth, db, app } from "../firebase";
import { encrypt } from "../utils/encryptUtil";
import { getFunctions, httpsCallable } from "firebase/functions";
import { useNavigate } from "react-router-dom";

export default function Signup() {
  const [firstName, setFirstName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [passwordStrength, setPasswordStrength] = useState("");
  const [otpSent, setOtpSent] = useState(false);
  const [otpCode, setOtpCode] = useState("");
  const [enteredOtp, setEnteredOtp] = useState("");
  const [showTerms, setShowTerms] = useState(false); // ✅ new state for popup
  const navigate = useNavigate();

  const functions = getFunctions(app, "us-central1");
  const sendOtpEmail = httpsCallable(functions, "sendOtpEmail");

  const checkPasswordStrength = (value) => {
    setPassword(value);
    const hasLetter = /[a-zA-Z]/.test(value);
    const hasNumber = /\d/.test(value);
    const hasSpecial = /[!@#$%^&*(),.?":{}|<>]/.test(value);
    const length = value.length >= 8;

    if (hasLetter && hasNumber && hasSpecial && length)
      setPasswordStrength("strong");
    else if (hasLetter && (hasNumber || hasSpecial))
      setPasswordStrength("medium");
    else setPasswordStrength("weak");
  };

  const getStrengthColor = () => {
    switch (passwordStrength) {
      case "strong":
        return "bg-green-500 w-3/4";
      case "medium":
        return "bg-yellow-500 w-1/2";
      case "weak":
        return "bg-red-500 w-1/4";
      default:
        return "bg-gray-300 w-1/6";
    }
  };

  const handleSignup = async (e) => {
    e.preventDefault();
    setError("");
    setSuccess("");

    try {
      await signOut(auth);
      const otp = Math.floor(100000 + Math.random() * 900000).toString();
      await sendOtpEmail({ to: email, code: otp });
      setOtpCode(otp);
      setOtpSent(true);
      setSuccess("✅ OTP sent! Please check your email and enter the code below.");
    } catch (err) {
      console.error("OTP send error:", err);
      setError("Failed to send OTP. Please try again.");
    }
  };

  const handleVerifyOtp = async (e) => {
    e.preventDefault();
    if (enteredOtp !== otpCode) {
      setError("Invalid OTP. Please try again.");
      return;
    }

    try {
      const userCred = await createUserWithEmailAndPassword(auth, email, password);
      const encEmail = encrypt(email);

      await setDoc(doc(db, "users", userCred.user.uid), {
        firstName,
        email: encEmail,
        preferences: {
          emailSubscription: "dailyTop10",
          dealAlerts: [],
        },
        createdAt: new Date(),
      });

      await signOut(auth);
      setSuccess("🎉 Signup successful! You can now log in.");
      setOtpSent(false);
      setFirstName("");
      setEmail("");
      setPassword("");
      setEnteredOtp("");
      setTimeout(() => {
        navigate("/login");
      }, 1500);
    } catch (err) {
      console.error("Signup error:", err.code, err.message);
      setError("Signup failed: " + err.message);
    }
  };

  return (
    <div className="flex items-center justify-center min-h-[85vh] bg-gray-50 px-4 relative">
      <div className="bg-white p-8 rounded-xl shadow-lg w-full max-w-md">
        <h2 className="text-2xl font-bold text-center text-indigo-600 mb-6">
          Create Account
        </h2>

        {/* STEP 1: Signup + Send OTP */}
        {!otpSent ? (
          <form onSubmit={handleSignup} className="space-y-4">
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-1">
                First Name
              </label>
              <input
                type="text"
                value={firstName}
                onChange={(e) => setFirstName(e.target.value)}
                required
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-400 focus:outline-none"
                placeholder="Enter your first name"
              />
            </div>

            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-1">
                Email
              </label>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-400 focus:outline-none"
                placeholder="Enter your email"
              />
            </div>

            <div className="relative">
              <label className="block text-sm font-semibold text-gray-700 mb-1">
                Password
              </label>
              <input
                type={showPassword ? "text" : "password"}
                value={password}
                onChange={(e) => checkPasswordStrength(e.target.value)}
                required
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-400 focus:outline-none pr-10"
                placeholder="Enter your password"
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute right-3 top-9 text-gray-500 hover:text-gray-700"
              >
                {showPassword ? "🙈" : "👁️"}
              </button>
              {password && (
                <div className="h-2 mt-2 w-full bg-gray-200 rounded-full">
                  <div
                    className={`h-2 rounded-full transition-all duration-300 ${getStrengthColor()}`}
                  ></div>
                </div>
              )}
            </div>

            <button
              type="submit"
              className="w-full py-2 bg-indigo-600 hover:bg-indigo-700 text-white font-semibold rounded-lg transition-all"
            >
              Sign Up
            </button>
          </form>
        ) : (
          // STEP 2: Enter OTP
          <form onSubmit={handleVerifyOtp} className="space-y-4">
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-1">
                Enter OTP
              </label>
              <input
                type="text"
                value={enteredOtp}
                onChange={(e) => setEnteredOtp(e.target.value)}
                maxLength={6}
                required
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-400 focus:outline-none"
                placeholder="Enter 6-digit code"
              />
            </div>

            <button
              type="submit"
              className="w-full py-2 bg-green-600 hover:bg-green-700 text-white font-semibold rounded-lg transition-all"
            >
              Verify & Create Account
            </button>
          </form>
        )}

        {error && <p className="mt-4 text-center text-red-600 font-medium">{error}</p>}
        {success && <p className="mt-4 text-center text-green-600 font-medium">{success}</p>}

        {/* Terms */}
        {!otpSent && (
          <p className="text-xs text-gray-600 mt-6 text-center">
            By signing up, you agree to our{" "}
            <span
              onClick={() => setShowTerms(true)}
              className="text-indigo-600 font-medium cursor-pointer hover:underline"
            >
              Terms and Conditions
            </span>
            .<br />
            We will not sell or use your information in any way.
            You will not receive notifications unless you subscribe to our newsletter or set a deal alert.
          </p>
        )}

        {/* Login link */}
        {!otpSent && (
          <div className="mt-4 text-center">
            <p className="text-sm text-gray-700">
              Already have an account?{" "}
              <button
                onClick={() => navigate("/login")}
                className="text-indigo-600 font-semibold hover:underline"
              >
                Login
              </button>
            </p>
          </div>
        )}
      </div>

      {/* ✅ Terms and Conditions Popup */}
      {showTerms && (
        <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-40 z-50 px-4">
          <div className="bg-white p-6 rounded-xl shadow-xl max-w-sm w-full text-gray-700 relative">
            <h3 className="text-lg font-semibold mb-3 text-center text-indigo-600">
              Terms and Conditions
            </h3>
            <div className="text-sm space-y-2 max-h-60 overflow-y-auto">
              <p>
                By creating an account, you agree to receive occasional updates and promotional emails related to deals and offers.
              </p>
              <p>
                We respect your privacy and will never sell or share your information with third parties without consent.
              </p>
              <p>
                You may unsubscribe or delete your account anytime from your profile settings.
              </p>
              <p>
                All deals are subject to change or expiration without notice. Pricing accuracy is not guaranteed.
              </p>
              <p>
                Continued use of the site indicates your acceptance of these terms.
              </p>
            </div>
            <button
              onClick={() => setShowTerms(false)}
              className="mt-5 w-full py-2 bg-indigo-600 hover:bg-indigo-700 text-white font-semibold rounded-lg transition-all"
            >
              Close
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
